/**
 * RegisterSelfHostModal (OCP-444) - operator form to register an existing
 * external / self-host orboto instance as a telemetry-only tenant.
 *
 * On success it hands the reveal-once secret payload back to the caller (which
 * shows SelfHostSecretModal). Instance URL is the only required field.
 */
import { useState } from 'react';

import { api, ApiError } from '../lib/api';
import type { SelfHostSecretReveal } from './SelfHostSecretModal';

interface RegisterResponse {
  tenant: { id: string; subdomain: string; self_host_url: string | null };
  beacon_secret: string;
  onboarding: { telemetry_endpoint: string; env: Record<string, string> };
}

export function RegisterSelfHostModal({
  onClose,
  onRegistered,
}: {
  onClose: () => void;
  onRegistered: (reveal: SelfHostSecretReveal) => void;
}) {
  const [instanceUrl, setInstanceUrl] = useState('');
  const [label, setLabel] = useState('');
  const [region, setRegion] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!instanceUrl.trim()) {
      setError('Instance URL is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, string> = { instance_url: instanceUrl.trim() };
      if (label.trim()) body.label = label.trim();
      if (region.trim()) body.region = region.trim();
      if (contactEmail.trim()) body.contact_email = contactEmail.trim();
      if (contactName.trim()) body.contact_name = contactName.trim();
      const r = await api<RegisterResponse>('/v1/admin/tenants/self-host', {
        method: 'POST',
        body,
      });
      onRegistered({
        beaconSecret: r.beacon_secret,
        onboarding: r.onboarding,
        subdomain: r.tenant.subdomain,
        selfHostUrl: r.tenant.self_host_url,
        rotated: false,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string } | null)?.error;
        if (code === 'invalid_url') setError('Instance URL must be a valid http(s) URL.');
        else if (code === 'already_registered')
          setError('That instance is already registered. Rotate its secret from the tenant detail page instead.');
        else if (code === 'subdomain_taken')
          setError('The derived label collides with an existing tenant. Set a distinct label.');
        else setError(`Could not register (HTTP ${err.status}).`);
      } else {
        setError('Could not register the instance.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Register self-host instance
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Mints a beacon secret for an existing external orboto instance (our dogfood, or a
          self-hosting customer) so its hourly telemetry lands here. No server / billing is created.
        </p>

        <div className="mt-4 space-y-3">
          <Labelled label="Instance URL" required>
            <input
              type="url"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              placeholder="https://orboto.example.com"
              autoComplete="off"
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Labelled>
          <Labelled label="Label (optional)" hint="Display subdomain; derived from the host when blank.">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="dogfood"
              autoComplete="off"
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Labelled>
          <div className="grid grid-cols-2 gap-3">
            <Labelled label="Region (optional)">
              <input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="self-host"
                autoComplete="off"
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              />
            </Labelled>
            <Labelled label="Contact email (optional)">
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="ops@example.com"
                autoComplete="off"
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              />
            </Labelled>
          </div>
          <Labelled label="Contact name (optional)">
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              autoComplete="off"
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </Labelled>
        </div>

        {error && (
          <div
            role="alert"
            className="mt-3 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting || !instanceUrl.trim()}
            className="rounded bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300"
          >
            {submitting ? 'Registering…' : 'Register + mint secret'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Labelled({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {hint && <span className="ml-1 text-xs text-gray-500">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
